/**
 * Canonical device classification — the frontend mirror of the gateway/backend
 * `DeviceType` enum and the proto `DeviceType`. The backend serializes the
 * device's `deviceType` as one of these lowercase strings.
 *
 * Keep this list in sync with `gateway/src/classification.rs` (`DeviceType`),
 * `gateway-api-proto` (`enum DeviceType`) and the backend's canonical strings.
 */
export const DEVICE_TYPES = [
  'router',
  'layer3_switch',
  'switch',
  'wap',
  'phone',
  'voip_phone',
  'host',
  'firewall',
  'wlc',
  'network_device',
  'gateway',
] as const;

export type DeviceType = (typeof DEVICE_TYPES)[number];

/**
 * Whether a device-type string denotes a wireless access point. Tolerates the
 * legacy `"ap"` alias that older rows may still carry (the canonical value is
 * `"wap"`).
 */
export function isWirelessAp(deviceType: string | null | undefined): boolean {
  return deviceType === 'wap' || deviceType === 'ap';
}

/** What a person reads for each device type. Never show the raw string. */
const DEVICE_TYPE_LABELS: Record<DeviceType, string> = {
  router: 'Router',
  layer3_switch: 'Layer 3 switch',
  switch: 'Switch',
  wap: 'Access point',
  phone: 'Phone',
  voip_phone: 'VoIP phone',
  host: 'Host',
  firewall: 'Firewall',
  wlc: 'Wireless LAN controller',
  network_device: 'Network device',
  gateway: 'Gateway',
};

/**
 * The friendly name for a device type. A value this build does not know (a
 * newer backend, the legacy `"ap"` alias) still reads as words rather than as
 * an identifier.
 */
export function deviceTypeLabel(deviceType: string | null | undefined): string {
  if (!deviceType) return 'Unknown';
  if (deviceType === 'ap') return DEVICE_TYPE_LABELS.wap;
  return (DEVICE_TYPE_LABELS as Record<string, string | undefined>)[deviceType] ?? humanizeIdentifier(deviceType);
}

/** `layer3_switch` / `someValue` → "Layer3 switch" / "Some value". */
export function humanizeIdentifier(value: string): string {
  const words = value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
